import Phaser from 'phaser';
import { type ShimmerClock, type PillToken } from '../objects/ShimmerClock.js';

export interface BankrollPillOptions {
  scene: Phaser.Scene;
  /** Centerline X for the bankroll text (canvas-buffer px). Provide this OR
   *  rightX. centerX → centered, large, no border (craps style). */
  centerX?: number;
  /** Right-edge X for the legacy right-anchored pill style (lobby). */
  rightX?: number;
  /** Top edge of the bankroll text (canvas-buffer px). */
  topY: number;
  /** Multiplier from intended-CSS-px to canvas-buffer-px. */
  dpr: number;
  onClick: () => void;
  /** Optional extra GameObjects (e.g. a Bezel's graphics) to scale + shake
   *  alongside the dollar text on win-pop. */
  scaleAlong?: Phaser.GameObjects.GameObject[];
  /** Optional shimmer clock — only honored in centered mode (text-only
   *  surface). Legacy right-anchored mode ignores this for now. */
  shimmerClock?: ShimmerClock | undefined;
}

/**
 * Compact top-right pill that toggles the full PlayerCard overlay. Mirrors the
 * BackToFloorPill's left-anchored counterpart so the chrome stays balanced.
 */
export class BankrollPill {
  private bg: Phaser.GameObjects.Graphics | undefined;
  private text: Phaser.GameObjects.Text;
  private hit: Phaser.GameObjects.Rectangle;
  private centerX: number | undefined;
  private rightX: number | undefined;
  private topY: number;
  private dpr: number;
  private cents = 0;
  private expanded = false;
  private hovered = false;
  private countTween: Phaser.Tweens.Tween | undefined;
  private scaleTween: Phaser.Tweens.Tween | undefined;
  private scaleAlong: Phaser.GameObjects.GameObject[];
  private shimmerClock: ShimmerClock | undefined;
  private shimmerToken: PillToken | undefined;
  private flashSuppressedUntilMs = 0;
  /** Rest text color, depends on mode: felt on the right-anchored gold
   *  pill (lobby), gold on the centered display-only pill
   *  (craps). Captured once at construction so all the color paths
   *  (shimmer, hover, insufficient-funds flash, scale tween) restore to
   *  the same baseline. */
  private restColor: string;
  /** Captured at construction so cleanup doesn't have to walk through
   *  this.text.scene — Phaser nulls a destroyed GameObject's .scene before
   *  SHUTDOWN handlers run, so reading off it crashes the teardown path. */
  private sceneEvents: Phaser.Events.EventEmitter | undefined;
  private destroyed = false;

  constructor(opts: BankrollPillOptions) {
    const { scene, centerX, rightX, topY, dpr, onClick } = opts;
    this.centerX = centerX;
    this.rightX = rightX;
    this.topY = topY;
    this.dpr = dpr;
    this.scaleAlong = opts.scaleAlong ?? [];
    this.shimmerClock = opts.shimmerClock;

    const isCentered = centerX !== undefined;
    const fontSize = isCentered ? Math.round(28 * dpr) : Math.round(11 * dpr);
    // Right-anchored mode flips to admin-login's gold button look: gold
    // bg, felt text. Centered mode stays gold text on no bg (the dollar
    // counter sits inside the craps bezel, so it keeps its own treatment).
    this.restColor = isCentered ? '#d4af37' : '#0d2c1d';
    if (!isCentered) this.bg = scene.add.graphics();
    this.text = scene.add
      .text(0, 0, '', {
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        fontSize: `${fontSize}px`,
        color: this.restColor,
        fontStyle: 'bold',
      })
      .setOrigin(isCentered ? 0.5 : 0, 0);
    this.hit = scene.add.rectangle(0, 0, 1, 1, 0x000000, 0);
    if (!isCentered) {
      // Legacy right-anchored pill remains tappable with hover affordance.
      // Hover now brightens the bg (gold → gold-light) rather than the
      // text, since the pill is gold-on-felt-text in its resting state.
      this.hit.setInteractive({ useHandCursor: true });
      this.hit.on('pointerdown', onClick);
      this.hit.on('pointerover', () => { this.hovered = true; this.redraw(); });
      this.hit.on('pointerout',  () => { this.hovered = false; this.redraw(); });
    }
    // Centered mode is display-only — no input, no hover.

    this.redraw();

    if (this.shimmerClock && this.centerX !== undefined) {
      this.shimmerToken = this.shimmerClock.register();
      this.sceneEvents = scene.events;
      scene.events.on(Phaser.Scenes.Events.UPDATE, this.tickShimmer);
      // Phaser scene EventEmitters survive scene.stop()/start(), so without
      // this cleanup the listener leaks across navigations: re-entering the
      // craps table re-activates the scene with a stale tickShimmer wired to
      // a destroyed Text, and `this.text.scene` is undefined → `scene.time`
      // throws on the next UPDATE and aborts the scene's update emit.
      scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());
    }
  }

  private redraw() {
    const dollars = Math.floor(this.cents / 100);

    if (this.centerX !== undefined) {
      // Centered, display-only: just the dollar amount, no chevron.
      this.text.setText(`$${dollars.toLocaleString()}`);
      this.text.setPosition(this.centerX, this.topY);
      return;
    }

    // Legacy right-anchored pill — keeps the chevron as an expand/collapse hint.
    // Gold-on-felt-text "login button" treatment lifted from the admin
    // login: gold fill (brighter on hover), no border.
    const chevron = this.expanded ? ' ▴' : ' ▾';
    this.text.setText(`$${dollars.toLocaleString()}${chevron}`);
    const padX = Math.round(14 * this.dpr);
    const padY = Math.round(7 * this.dpr);
    const w = this.text.width + padX * 2;
    const h = this.text.height + padY * 2;
    const x = (this.rightX ?? 0) - w;
    const y = this.topY;
    this.text.setPosition(x + padX, y + padY);
    this.bg?.clear();
    this.bg?.fillStyle(this.hovered ? 0xf4d35e : 0xd4af37, 1);
    this.bg?.fillRect(x, y, w, h);
    this.hit.setPosition(x + w / 2, y + h / 2).setSize(w, h);
  }

  private tickShimmer = (): void => {
    if (!this.shimmerClock || this.shimmerToken === undefined) return;
    if (this.centerX === undefined) return;
    const scene = this.text.scene;
    if (this.scaleTween?.isPlaying() || this.countTween?.isPlaying()) {
      this.text.setColor('#d4af37');
      return;
    }
    if (scene.time.now < this.flashSuppressedUntilMs) {
      // flashInsufficientFunds owns the color until it finishes.
      return;
    }
    const screenW = scene.scale.width;
    const phase = this.shimmerClock.localPhase(this.shimmerToken, this.text.x, screenW);
    if (phase === null) {
      this.text.setColor('#d4af37');
      return;
    }
    // Triangle wave via sine — smooth ease-in / ease-out on both ends.
    const t = Math.sin(phase * Math.PI);
    const rest = Phaser.Display.Color.IntegerToRGB(0xd4af37);
    const peak = Phaser.Display.Color.IntegerToRGB(0xfff7c2);
    const c = Phaser.Display.Color.Interpolate.RGBWithRGB(
      rest.r, rest.g, rest.b, peak.r, peak.g, peak.b, 100, Math.round(t * 100),
    );
    const hex = '#' + ((c.r << 16) | (c.g << 8) | c.b).toString(16).padStart(6, '0');
    this.text.setColor(hex);
  };

  setBankrollCents(cents: number, opts: { animate?: boolean } = {}) {
    const prev = this.cents;
    // Snap on first ever set, on a decrease, on a tiny change, or when the
    // caller explicitly says no pop (e.g. bet recall — money came back but
    // it isn't a win).
    const delta = cents - prev;
    const animate = opts.animate !== false;
    if (!animate || prev <= 0 || delta <= 0 || delta < 100) {
      this.cents = cents;
      this.redraw();
      return;
    }
    this.countUpTo(cents);
  }

  private countUpTo(target: number) {
    this.countTween?.stop();
    this.scaleTween?.stop();
    const scene = this.text.scene;
    const start = this.cents;
    const delta = target - start;
    // Animation shape per the design wireframe:
    //   Phase 1 (rise, t1):  slow accelerating ramp from 1 → peakScale.
    //                        Count ticks up over the same window.
    //   Phase 2 (plateau):   brief hold at peakScale showing the final
    //                        number large; shake fires here.
    //   Phase 3 (fall, t2):  quick drop back to 1. t1 = 2 * t2.
    const durationMs = Math.min(1050, 180 + Math.sqrt(delta) * 6);
    // Rise stays leisurely; plateau holds longer; drop snaps back faster.
    const t1 = Math.max(170, Math.round(durationMs * 0.35));
    const plateau = Math.max(220, Math.round(durationMs * 0.32));
    const t2 = Math.max(40, Math.round(durationMs * 0.07));
    const peakScale = 1 + Math.min(0.8, Math.sqrt(delta) / 200);
    // Bezel grows less dramatically than the number — keeps the chrome
    // legible during the pop instead of swallowing the whole top of the
    // screen.
    const bezelPeak = 1 + (peakScale - 1) * 0.45;
    this.scaleTween = scene.tweens.add({
      targets: this.text,
      scale: { from: 1, to: peakScale },
      duration: t1,
      // Cubic.Out — fast initial pop, decelerating into the plateau.
      ease: 'Cubic.Out',
      onUpdate: (tw) => {
        // Drive the bezel at a dampened version of the text's scale —
        // softer growth so the chrome doesn't swallow the screen.
        const a = (tw as Phaser.Tweens.Tween).getValue() ?? 1;
        const norm = (a - 1) / Math.max(0.0001, peakScale - 1);
        const b = 1 + norm * (bezelPeak - 1);
        for (const obj of this.scaleAlong) (obj as unknown as { setScale: (s: number) => void }).setScale?.(b);
      },
      onComplete: () => {
        this.shakeFinal();
        scene.time.delayedCall(plateau, () => {
          // Phase 3: quick drop back to rest.
          this.scaleTween = scene.tweens.add({
            targets: this.text,
            scale: { from: peakScale, to: 1 },
            duration: t2,
            ease: 'Cubic.In',
            onUpdate: (tw) => {
              const a = (tw as Phaser.Tweens.Tween).getValue() ?? 1;
              const norm = (a - 1) / Math.max(0.0001, peakScale - 1);
              const b = 1 + norm * (bezelPeak - 1);
              for (const obj of this.scaleAlong) (obj as unknown as { setScale: (s: number) => void }).setScale?.(b);
            },
            onComplete: () => {
              this.text.setScale(1);
              for (const obj of this.scaleAlong) (obj as unknown as { setScale: (s: number) => void }).setScale?.(1);
            },
          });
        });
      },
    });
    // Count finishes alongside the rise so the player sees the final
    // number while the text is at peak size.
    this.countTween = scene.tweens.addCounter({
      from: start,
      to: target,
      duration: t1,
      ease: 'Cubic.Out',
      onUpdate: (tw) => {
        this.cents = Math.floor((tw as Phaser.Tweens.Tween).getValue() ?? target);
        this.redraw();
      },
      onComplete: () => {
        this.cents = target;
        this.redraw();
      },
    });
  }

  /** Hard rattle when the count finishes — punctuates the win. */
  private shakeFinal() {
    const scene = this.text.scene;
    const baseX = this.text.x;
    const baseY = this.text.y;
    const amp = Math.max(6, Math.round(10 * this.dpr));
    // Brighten during the shake — restColor → near-white — then restore.
    const restColor = this.restColor;
    const flashColor = '#fff7c2';
    this.text.setColor(flashColor);
    // Random 2-D offsets with decaying amplitude — feels more like a kick
    // than a metronome. ~12 ticks * 22ms ≈ 264ms shake.
    const TICKS = 12;
    let i = 0;
    const tick = () => {
      const decay = 1 - i / TICKS;
      const ox = (Math.random() * 2 - 1) * amp * decay;
      const oy = (Math.random() * 2 - 1) * amp * decay;
      this.text.setPosition(baseX + ox, baseY + oy);
      i++;
      if (i >= TICKS) {
        this.text.setPosition(baseX, baseY);
        this.text.setColor(restColor);
        return;
      }
      scene.time.delayedCall(22, tick);
    };
    tick();
  }

  setExpanded(expanded: boolean) {
    if (this.expanded === expanded) return;
    this.expanded = expanded;
    this.redraw();
  }

  /** Top-right corner of the dollar text — handy as a toast anchor. */
  getAnchor(): { x: number; y: number } {
    return { x: this.text.x + this.text.width, y: this.text.y };
  }

  setDepth(depth: number) {
    this.text.setDepth(depth);
    this.hit.setDepth(depth);
  }

  /**
   * Pulse the bankroll text in red briefly — used when the player tries an
   * action they can't afford, drawing the eye to "you're out of money" rather
   * than silently disabling the action.
   */
  flashInsufficientFunds() {
    const restColor = this.restColor;
    const flashColor = '#ff5d5d';
    const scene = this.text.scene;
    this.flashSuppressedUntilMs = scene.time.now + 5 * 90 + 50;
    let toggle = true;
    this.text.setColor(flashColor);
    for (let i = 1; i <= 5; i++) {
      const delay = i * 90;
      const next = toggle ? restColor : flashColor;
      toggle = !toggle;
      scene.time.delayedCall(delay, () => this.text.setColor(next));
    }
    // A small scale bounce on top of the color flash for extra peripheral pop.
    scene.tweens.add({
      targets: this.text,
      scale: 1.18,
      duration: 110,
      ease: 'Sine.Out',
      yoyo: true,
    });
  }

  destroy() {
    if (this.destroyed) return;
    this.destroyed = true;
    // Use the scene events emitter we captured at construction — by the
    // time SHUTDOWN handlers fire, this.text.scene may already be nulled.
    this.sceneEvents?.off(Phaser.Scenes.Events.UPDATE, this.tickShimmer);
    this.sceneEvents = undefined;
    if (this.shimmerClock && this.shimmerToken !== undefined) {
      this.shimmerClock.unregister(this.shimmerToken);
      this.shimmerToken = undefined;
    }
    this.bg?.destroy();
    this.text.destroy();
    this.hit.destroy();
  }
}
